import Image from "@/components/Image";
import { TelInput } from "@/components/TelInput";
import { useCountDown, useMemoizedFn } from "ahooks";
import { Button, Form } from "antd";
import classNames from "classnames";
import { useCallback, useRef, useState } from "react";
import { resetPwd, resetVerify, sendSms } from "../../../services";
import HBInput from "../../HBInput";
import HBMessage from "../../HBMessage";
import HBConfirm from "../../HBModal/confirm";
import VerifyInput from "../../Verify/VerifyInput";
import styles from "../index.module.less";
import rules from "../shared/rules";

export default function ResetPwd({ initialTel, onOk, onCancel }) {
  const [step, setStep] = useState(1);
  const formRef = useRef();
  const dataRef = useRef(null);
  const verifyRef = useRef(null);

  const AccountType = {
    email: 0,
    tel: 1,
  };
  const [accountType, setAccountType] = useState(AccountType.tel);
  const isTelType = accountType === AccountType.tel;

  // 验证码发送倒计时
  const [smsSendTime, setSmsSendTime] = useState();
  const [smsSendCountDown] = useCountDown({
    targetDate: smsSendTime,
  });

  const verifyTel = useMemoizedFn(async () => {
    let value;
    try {
      value = await formRef.current.validateFields();
    } catch (error) {
      verifyRef.current.reset();
      return;
    }

    dataRef.current = value;

    // 验证邮箱
    if (!isTelType) {
      const result = await resetVerify({
        email: value.tel,
        validate: value.validate,
      });

      if (result.err) {
        verifyRef.current.reset();
        return;
      }

      if (result.error) {
        HBMessage.error(result.error);
        verifyRef.current.reset();
        return;
      }

      HBConfirm.open({
        type: "success",
        title: "提示",
        content:
          "重置密码的链接已被发送到你的邮箱，请点击邮件内链接进行重置密码。",
        okText: "确认",
      });
      onOk();
    } else {
      const result = await resetVerify({
        ...value,
        tel: value.tel.value,
      });

      if (result.err) {
        verifyRef.current.reset();
        return;
      }

      if (result.error) {
        HBMessage.error(result.error);
        verifyRef.current.reset();
        return;
      }
      setSmsSendTime(Date.now() + 60000);
      setStep(2);
    }
  });

  const sendSmsClick = useCallback(async () => {
    await sendSms({
      reason: "resendResetPassword",
      tel: dataRef.current.tel?.value || dataRef.current.tel,
      validate: dataRef.current.validate,
    });

    setSmsSendTime(Date.now() + 60000);
  }, [setSmsSendTime]);

  const onResetPwd = useCallback(async () => {
    const value = await formRef.current.validateFields();
    if (value.password2 === value.password1) {
      const result = await resetPwd({
        ...value,
        tel: dataRef.current.tel?.value || dataRef.current.tel,
      });

      // 错误处理
      if (result.error || result.err) {
        switch (result.error) {
          case "验证码错误":
            formRef.current.setFields([
              {
                name: "captcha",
                errors: [result.error],
              },
            ]);
            break;
          case "参数校验错误":
            const err = [];
            for (const key in result.data) {
              err.push({
                name: key,
                errors: result.data[key],
              });
            }
            formRef.current.setFields(err);
            break;

          default:
            HBMessage.error(result.error || result.msg || "密码重置失败");
            break;
        }
        return;
      }

      // 成功处理
      HBMessage.success("密码重置成功!");
      setStep(3);
      return;
    }

    formRef.current.setFields([
      {
        name: "password2",
        errors: ["密码和确认密码不一致"],
      },
    ]);
  }, []);

  if (step !== 3) {
    return (
      <div className={styles.reset}>
        <div className={styles.title}>重置密码</div>
        <Form className={styles.form} ref={formRef}>
          <Form.Item
            className={styles.item}
            name="tel"
            rules={isTelType ? rules.tel : rules.email}
            initialValue={isTelType ? initialTel : ""}
          >
            {isTelType ? (
              <TelInput placeholder="手机号" disabled={step === 2} autoFocus />
            ) : (
              <HBInput placeholder="邮箱" disabled={step === 2} autoFocus />
            )}
          </Form.Item>

          {step === 1 && (
            <>
              <Form.Item
                className={classNames([styles.item, styles.short])}
                name="validate"
              >
                <VerifyInput ref={verifyRef} type="reset" onOk={verifyTel} />
              </Form.Item>
              {isTelType ? (
                <a
                  onClick={() => {
                    setAccountType(AccountType.email);
                    formRef.current.setFieldsValue({ tel: "" });
                  }}
                >
                  邮箱重置密码
                </a>
              ) : (
                <a
                  onClick={() => {
                    setAccountType(AccountType.tel);
                    formRef.current.resetFields();
                  }}
                >
                  手机重置密码
                </a>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <Form.Item
                className={classNames([styles.item, styles.long])}
                name="captcha"
                rules={rules.captcha}
              >
                <HBInput
                  placeholder="验证码"
                  addonAfter={
                    <Button
                      disabled={smsSendCountDown > 0}
                      onClick={sendSmsClick}
                    >
                      {smsSendCountDown > 0
                        ? `重新发送 ${Math.round(smsSendCountDown / 1000)}`
                        : "发送验证码"}
                    </Button>
                  }
                />
              </Form.Item>
              <Form.Item
                className={styles.item}
                name="password1"
                rules={rules.password}
              >
                <HBInput placeholder="设定新密码" type="password" />
              </Form.Item>
              <Form.Item
                className={classNames([styles.item, styles.last])}
                name="password2"
                rules={rules.password}
              >
                <HBInput placeholder="请再输入一次新密码" type="password" />
              </Form.Item>
            </>
          )}
        </Form>
        {step === 1 ? (
          <Button className={styles.button} type="primary" onClick={onCancel}>
            返回
          </Button>
        ) : (
          <Button className={styles.button} type="primary" onClick={onResetPwd}>
            确认修改
          </Button>
        )}
      </div>
    );
  } else {
    return <ResetSuccess onOk={onOk} />;
  }
}

export function EmailResetPwd({ onOk, token }) {
  const formRef = useRef();
  const [step, setStep] = useState(1);

  const onResetPwd = useCallback(async () => {
    const value = await formRef.current.validateFields();
    if (value.password2 === value.password1) {
      const result = await resetPwd({ ...value, token: token });

      // 错误处理
      if (result.error) {
        HBMessage.error(result.error);
        return;
      }

      // 成功处理
      HBMessage.success("密码重置成功!");
      setStep(2);
      return;
    }

    formRef.current.setFields([
      {
        name: "password2",
        errors: ["密码和确认密码不一致"],
      },
    ]);
  }, [token]);

  // 成功页面
  if (step === 2) return <ResetSuccess onOk={onOk} />;

  return (
    <div className={styles.reset}>
      <div className={styles.title}>重置密码</div>
      <Form className={styles.form} ref={formRef}>
        <Form.Item
          className={styles.item}
          name="password1"
          rules={rules.password}
        >
          <HBInput placeholder="设定新密码" type="password" />
        </Form.Item>
        <Form.Item
          className={classNames([styles.item, styles.last])}
          name="password2"
          rules={rules.password}
        >
          <HBInput placeholder="请再输入一次新密码" type="password" />
        </Form.Item>
      </Form>
      <Button className={styles.button} type="primary" onClick={onResetPwd}>
        确认修改
      </Button>
    </div>
  );
}

function ResetSuccess({ onOk }) {
  const [targetDate] = useState(Date.now() + 5000);

  const [countDownSecond] = useCountDown({
    targetDate,
    onEnd: () => {
      onOk();
    },
  });
  return (
    <div className={styles.resetSuccess}>
      <Image
        src="https://st0.dancf.com/static/02/202204180656-dc9b.svg"
        alt="重置成功"
      />
      <div className={styles.title}>密码重置成功</div>
      <Button className={styles.button} type="primary" onClick={onOk}>
        登录花瓣 {Math.round(countDownSecond / 1000)}s
      </Button>
    </div>
  );
}
